import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { NewslettersCreateNewsletterFields } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { HookMutationConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { newsletterKeys, parseNewsletter, type Newsletter } from './entity';

/**
 * Newsletter (one-time send) writes.
 *
 * A send made from a data-index item is two writes: the create, and a
 * `recipients` update setting `filters` to the item, which is what makes
 * the send about that attribute (`~/code/ui/app/utils/data-index/shared.ts:864-878`).
 * Both are here.
 */

/**
 * What a new one-time send starts as.
 *
 * `send_percentage` is required by the backend and has no server-side
 * default — the page this replaces only ever sets a name because its model
 * declares `defaultValue: 100` and serializes the whole record
 * (`app/models/newsletter.ts:581`). Sending it explicitly is that default
 * made visible rather than a choice made here.
 */
export const FULL_SEND_PERCENTAGE = 100;

export interface CreateNewsletterInput {
  workspaceId: number;
  newsletter: NewslettersCreateNewsletterFields;
}

/**
 * Creates a one-time send and returns it.
 *
 * Typed against the generated request rather than sent raw: newsletters
 * publishes a request schema narrowed to what create accepts, so the
 * generated type says `name` and `send_percentage` and means it. The
 * campaign create beside this one cannot do the same, because its schema
 * is the whole update surface.
 *
 * @public
 */
export async function createNewsletter(input: CreateNewsletterInput): Promise<Newsletter> {
  const raw = await hydraUiApi.newsletters.create({
    params: { path: { environment_id: String(input.workspaceId) } },
    body: { newsletter: input.newsletter },
  });
  // The response declares `newsletter` optional, though a successful
  // create always carries one. Checked rather than asserted, the same way
  // `parseCampaignModel` handles the identical shape.
  if (raw.newsletter == null) {
    throw new Error('missing newsletter in create response');
  }
  return parseNewsletter(raw.newsletter);
}

type UseCreateNewsletterVariables = Omit<CreateNewsletterInput, 'workspaceId'>;

interface UseCreateNewsletterOptions {
  /** Override the workspace id; defaults to `requireWorkspaceId()` (current URL). */
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<Newsletter, UseCreateNewsletterVariables>;
}

/**
 * Write-through: the created send is put in its detail slot, and the lists
 * and the catalog are invalidated so either one already on screen shows it.
 *
 * @public
 */
export const useCreateNewsletter = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: UseCreateNewsletterOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};

  return useMutation({
    ...rest,
    mutationFn: (variables: UseCreateNewsletterVariables) => createNewsletter({ workspaceId, ...variables }),
    onSuccess: (newsletter, ...callbackArgs) => {
      qc.setQueryData(newsletterKeys.detail(workspaceId, newsletter.id), newsletter);
      // `lists()` and the catalog prefix rather than `all()`: the latter
      // covers the detail slot written a line above, throwing away the
      // record this mutation just fetched.
      void qc.invalidateQueries({ queryKey: newsletterKeys.lists(workspaceId) });
      void qc.invalidateQueries({ queryKey: [...newsletterKeys.all(workspaceId), 'catalog'] });
      onSuccess?.(newsletter, ...callbackArgs);
    },
  });
};

/**
 * The `recipients` update that gives a send its audience.
 *
 * `filters` is base64, not an array and not JSON text. The backend binds
 * it as `*string` and stores it verbatim, so it accepts either — but the
 * page that reads it back decodes it through a `base64-json` transform
 * (`~/code/ui/app/models/newsletter.ts:199`), and raw JSON throws there
 * and resolves to no audience at all. `queryEncode` is the producer;
 * every stored fixture is base64 of the URI-encoded array.
 */
export type NewsletterRecipientsUpdate = {
  update_type: 'recipients';
  filters: string;
} & NewsletterSendingOptions;

/**
 * The four sending options a `recipients` update must restate. The handler
 * validates each as required — the booleans as non-null — so an update
 * carrying only `filters` is a 422 (`ui_api/newsletters/update.go:754-766`).
 * The page this replaces never notices because it saves the whole record;
 * a caller here passes back the values the send was created with.
 */
export type NewsletterSendingOptions = Pick<
  Newsletter,
  'send_percentage' | 'send_to_unsubscribed' | 'deduped' | 'use_message_limits'
>;

export interface UpdateNewsletterRecipientsInput {
  workspaceId: number;
  newsletterId: string;
  newsletter: NewsletterRecipientsUpdate;
}

/**
 * Points a one-time send at the people it should go to.
 *
 * @public
 */
export async function updateNewsletterRecipients(input: UpdateNewsletterRecipientsInput): Promise<Newsletter> {
  const raw = await hydraUiApi.newsletters.update({
    params: { path: { environment_id: String(input.workspaceId), id: input.newsletterId } },
    // Raw for the same reason the campaign updates are: the generated
    // request is the union of every update type with nothing optional.
    rawBody: JSON.stringify({ newsletter: input.newsletter }),
    headers: { 'Content-Type': 'application/json' },
  });
  if (raw.newsletter == null) {
    throw new Error('missing newsletter in update response');
  }
  return parseNewsletter(raw.newsletter);
}
